// Wrapper para la seccion /catalog. Muestra selector de partner (persistido
// en localStorage) + tabs Tipos / Items. Todas las paginas de catalog se
// renderizan adentro y comparten el partnerId via outlet context.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listPartners, type PartnerListItem } from '@/lib/api';
import { fieldCls } from '@/lib/form-styles';

const PARTNER_STORAGE_KEY = 'authsys-admin-catalog-partner';

export interface CatalogContext {
  partnerId: string;
  partners: PartnerListItem[];
}

export function useCatalogContext(): CatalogContext {
  return useOutletContext<CatalogContext>();
}

export default function CatalogShell(): JSX.Element {
  const navigate = useNavigate();
  const partnersQ = useQuery({ queryKey: ['admin-partners'], queryFn: listPartners });

  // Hidratacion del partner activo desde localStorage. Si no hay ninguno
  // guardado o el guardado ya no existe, usamos el primero del listado.
  const [partnerId, setPartnerId] = useState<string>(() => {
    try {
      return localStorage.getItem(PARTNER_STORAGE_KEY) ?? '';
    } catch {
      return '';
    }
  });

  useEffect(() => {
    if (!partnersQ.data || partnersQ.data.length === 0) return;
    const valid = partnersQ.data.some((p) => p.partner_id === partnerId);
    if (!valid) {
      const first = partnersQ.data[0]!.partner_id;
      setPartnerId(first);
      try {
        localStorage.setItem(PARTNER_STORAGE_KEY, first);
      } catch {
        // localStorage indisponible (private mode, etc.) — uso runtime nomas.
      }
    }
  }, [partnersQ.data, partnerId]);

  function handlePartnerChange(next: string): void {
    setPartnerId(next);
    try {
      localStorage.setItem(PARTNER_STORAGE_KEY, next);
    } catch {
      // ignore
    }
    // Al cambiar de partner, los detalles de tipo/item del partner anterior
    // dejan de aplicar — volvemos al listado para evitar pantallas en blanco.
    navigate('/catalog/types');
  }

  const context: CatalogContext = useMemo(
    () => ({ partnerId, partners: partnersQ.data ?? [] }),
    [partnerId, partnersQ.data],
  );

  if (partnersQ.isLoading) {
    return <p className="text-sm text-ink-muted">Cargando partners…</p>;
  }
  if (partnersQ.isError || !partnersQ.data) {
    return (
      <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
        No pudimos cargar la lista de partners.
      </div>
    );
  }
  if (partnersQ.data.length === 0) {
    return (
      <p className="text-sm text-ink-muted">
        No hay partners configurados. Crea uno desde la seccion <strong>Partners</strong> antes de gestionar el catalogo.
      </p>
    );
  }

  const tabCls = ({ isActive }: { isActive: boolean }): string =>
    `px-4 py-2 text-sm border-b-2 -mb-px transition ${
      isActive
        ? 'border-brand text-brand font-medium'
        : 'border-transparent text-ink-muted hover:text-ink dark:hover:text-ink-inverted'
    }`;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 data-help-page="catalog.page" data-help="catalog.page" className="text-2xl font-semibold">
            Catálogo
          </h1>
          <p className="text-sm text-ink-muted max-w-2xl">
            Tipos e items que los usuarios pueden marcar como favoritos. Cada
            tipo define un template de campos custom que se piden al cargar los
            items de ese tipo.
          </p>
        </div>
        <label className="text-sm space-y-1" data-help="catalog.partner">
          <span className="font-medium block">Partner</span>
          <select
            value={partnerId}
            onChange={(e) => handlePartnerChange(e.target.value)}
            className={fieldCls({ size: 'lg', block: false, extra: 'min-w-[14rem]' })}
          >
            {partnersQ.data.map((p) => (
              <option key={p.partner_id} value={p.partner_id}>
                {p.display_name}
              </option>
            ))}
          </select>
        </label>
      </header>

      <nav
        aria-label="Secciones de catalogo"
        className="flex gap-1 border-b border-surface-border dark:border-surface-dark-border"
      >
        <NavLink to="/catalog/types" data-help="catalog.types" className={tabCls}>
          Tipos
        </NavLink>
        <NavLink to="/catalog/items" data-help="catalog.items" className={tabCls}>
          Items
        </NavLink>
      </nav>

      <section>{partnerId ? <Outlet context={context} /> : null}</section>
    </div>
  );
}

export function CatalogReadOnlyBanner({ canWrite }: { canWrite: boolean }): ReactNode {
  if (canWrite) return null;
  return (
    <div role="status" className="rounded-lg border border-status-warning/30 bg-status-warning/10 p-3 text-sm text-status-warning">
      Tu perfil tiene solo lectura sobre Catálogo. Crear/editar/archivar está deshabilitado.
    </div>
  );
}
